import {
  computeInteraction,
  MTK_DECIMALS,
  PortfolioState as PortoflioStateBussinessLogic,
} from '@meteraprotocol/core';
import { BigRational } from 'big-rational-ts';
import BigNumber from 'bignumber.js';
import { getAddressDetails } from 'lucid-txpipe';
import {
  CreateOrderDeposit,
  CreateOrderWithdraw,
  OrdersSubmitPostRequest,
  SDKApiFactory,
  SwapCreatePostRequest,
  SwapSubmitPostRequest,
} from '../../../api/api.js';
import { Configuration } from '../../../api/configuration.js';
import {
  createOrderResponseSchema,
  createSwapResponseSchema,
  IPortfolioState,
  WalletData,
} from '../../types.js';
import { bigRationalToBigNumber, dbNumericToBigRational } from '../../utils.js';
import {
  APIEventHandlers,
  decimalToBigInt,
  getWalletBalance,
  handleApiError,
  isError,
} from './index.js';
import { signTx } from './signTx.js';

/**
 * Turns an object of type PortfolioState into an object that
 * can be used to compute the amount of tokens needed
 * (returned) given an amount of ADA
 *
 * @param state - The state the portfolio is in before the deposit/withdrawal
 * @returns A price, weight dictionary for each asset with keys of assetId.
 * And a dict with how many tokens there are in the portfolio and the supply
 * of MTK
 */
export function formatState(state: IPortfolioState) {
  const weights: Record<string, BigRational> = {};
  const prices: Record<string, BigRational> = {};
  const amountsPerAsset: Record<string, bigint> = {};
  for (const asset of state.assets) {
    prices[asset.asset.id] = dbNumericToBigRational(asset.price).mul(
      // correct price of asset to the right decimal
      new BigRational(1n, BigInt(10 ** asset.asset.decimals)),
    );
    weights[asset.asset.id] = new BigRational(
      asset.weightNum,
      asset.weightDenom,
    );
    amountsPerAsset[asset.asset.id] = BigInt(asset.amount);
  }
  return {
    prices,
    weights,
    state: { assets: amountsPerAsset, mtkSupply: state.supply },
  };
}

/**
 * Wraps a call to `computeInteraction` for the UI.
 * @param inputValue - The amount of ADA to be deposited / withdrawn.
 * @param formattedState - The state the portfolio is in before the interaction.
 * @returns How much per asset needs to be deposited(withdrawn) and how many MTKs
 * are minted (burned)
 */
export function computeAmountPerAssetForInteraction(
  adaAmount: BigRational,
  formattedState: ReturnType<typeof formatState>,
): { assets: Record<string, bigint>; mtkSupply: bigint } {
  if (adaAmount.eq(new BigRational(0n, 1n))) {
    const zeroPerAsset: Record<string, bigint> = {};
    for (const [id] of Object.entries(formattedState.state.assets)) {
      zeroPerAsset[id] = 0n;
    }
    return {
      assets: zeroPerAsset,
      mtkSupply: 0n,
    };
  }
  const stateAfterInteraction = computeInteraction(
    formattedState.prices,
    formattedState.weights,
    formattedState.state,
    adaAmount,
  );
  // console.log({ nextState: stateAfterInteraction });
  const roundingMode = adaAmount.gte(new BigRational(0n, 1n))
    ? 'ceil'
    : 'floor';

  const amountPerAssetDiff: Record<string, bigint> = {};
  for (const [assetId, amount] of Object.entries(formattedState.state.assets)) {
    const diffRat = stateAfterInteraction.assets[assetId].subtract(
      new BigRational(amount, 1n),
    );
    amountPerAssetDiff[assetId] = decimalToBigInt(
      bigRationalToBigNumber(diffRat),
      roundingMode,
    );
  }
  const mtkSupply =
    decimalToBigInt(
      bigRationalToBigNumber(stateAfterInteraction.mtkSupply),
      roundingMode,
    ) - formattedState.state.mtkSupply;

  return { assets: amountPerAssetDiff, mtkSupply };
}

/**
 * Creates an order in the backend. In case of error, it will call the `onError`
 * callback with the error message.
 */
export async function handleOrderCreation(
  wallet: WalletData,
  portfolio: IPortfolioState,
  amountsPerAsset: PortoflioStateBussinessLogic<bigint, bigint>,
  inputValue: BigNumber,
  apiBaseUrl: string,
  swapId: string | null,
  { onError, onSuccess }: APIEventHandlers,
) {
  const config = new Configuration({ basePath: apiBaseUrl });
  const sdk = SDKApiFactory(config);
  try {
    const balance = await getWalletBalance(wallet.wallet);
    if (balance['lovelace'] < 10_000_000n) {
      return onError('You need more tADA in your wallet to do this');
    }

    let interactionPayload: CreateOrderDeposit | CreateOrderWithdraw;
    const tokens = Object.entries(amountsPerAsset.assets).map(([k, v]) => ({
      id: k,
      amount: v.toString(),
    }));
    if (inputValue.gt(0)) {
      const deposit: CreateOrderDeposit = {
        address: getAddressDetails(await wallet.wallet.getChangeAddress())
          .address.bech32,
        portfolioId: portfolio.portfolio.id,
        maxBatcherFee: portfolio.batcherFee.toString(),
        minMtkAcceptable: '1',
        tokens,
      };
      interactionPayload = deposit;
    } else {
      const withdraw: CreateOrderWithdraw = {
        address: getAddressDetails(await wallet.wallet.getChangeAddress())
          .address.bech32,
        portfolioId: portfolio.portfolio.id,
        amount: decimalToBigInt(
          inputValue.times(10 ** MTK_DECIMALS).abs(),
          'floor',
        ).toString(),
        tokens,
        maxBatcherFee: portfolio.batcherFee.toString(),
        minWorthAcceptable: '1',
      };
      interactionPayload = withdraw;
    }

    const createOrderResponse = await sdk
      .ordersCreatePost(interactionPayload)
      .catch(handleApiError);

    if (isError(createOrderResponse)) {
      return onError(createOrderResponse.error);
    }
    const { cbor, id } = createOrderResponseSchema.parse(
      createOrderResponse.data,
    );
    const signedTx = await signTx(wallet.wallet, cbor, false);
    const payloadSubmit: OrdersSubmitPostRequest = { id, cbor: signedTx };
    const submitOrderResponse = await sdk
      .ordersSubmitPost(payloadSubmit)
      .catch(handleApiError);

    if (isError(submitOrderResponse)) {
      return onError(submitOrderResponse.error);
    }
    onSuccess(id);
    return id;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    console.error(err);
    onError('Unknown error. Try again later');
  }
}

function substractAssets(
  assets: Record<string, bigint>,
  assetsToSubstract: Record<string, bigint>,
): Record<string, bigint> {
  let result = { ...assets };
  for (const [asset, amount] of Object.entries(assetsToSubstract)) {
    if (assets[asset]) {
      result[asset] = assets[asset] - amount;
    }
  }
  return result;
}

function cleanLEZero(assets: Record<string, bigint>) {
  for (const [asset, amount] of Object.entries(assets)) {
    if (amount <= 0) {
      delete assets[asset];
    }
  }
}

/**
 * Creates a swap order in the backend. In case of error, it will call the `onError`
 * callback with the error message.
 */
export async function handleSwapCreation(
  wallet: WalletData,
  portfolio: IPortfolioState,
  assetsInWallet: Record<string, bigint>,
  assetsRequired: Record<string, bigint>,
  totalInput: bigint,
  swapDeficit: boolean,
  apiBaseUrl: string,
  { onError, onSuccess }: APIEventHandlers,
) {
  const config = new Configuration({ basePath: apiBaseUrl });
  const sdk = SDKApiFactory(config);
  try {
    const balance = await getWalletBalance(wallet.wallet);
    if (balance['lovelace'] < 10_000_000n) {
      return onError('You need more ADA in your wallet to do this');
    }
    let swapAssets = { ...assetsRequired };
    let userAssetsInvolved: Record<string, bigint> = {};
    if (swapDeficit) {
      swapAssets = substractAssets(assetsRequired, assetsInWallet);
      userAssetsInvolved = substractAssets(assetsRequired, swapAssets);
      cleanLEZero(userAssetsInvolved);
    }
    cleanLEZero(swapAssets);

    const payload: SwapCreatePostRequest = {
      address: getAddressDetails(await wallet.wallet.getChangeAddress()).address
        .bech32,
      maxBatcherFee: portfolio.batcherFee.toString(),
      totalAmount: totalInput.toString(),
      userAssets: Object.entries(userAssetsInvolved).map(([k, v]) => ({
        id: k,
        amount: v.toString(),
      })),
      swap: Object.entries(swapAssets).map(([k, v]) => ({
        id: k,
        amount: v.toString(),
      })),
      portfolioId: portfolio.portfolio.id,
    };

    const createSwapResponse = await sdk
      .swapCreatePost(payload)
      .catch(handleApiError);

    if (isError(createSwapResponse)) {
      return onError(createSwapResponse.error);
    }
    const { cbor, txId } = createSwapResponseSchema.parse(
      createSwapResponse.data,
    );
    const signedTx = await signTx(wallet.wallet, cbor, false, 'conway');
    const payloadSubmit: SwapSubmitPostRequest = { cbor: signedTx };
    const submitOrderResponse = await sdk
      .swapSubmitPost(payloadSubmit)
      .catch(handleApiError);
    if (isError(submitOrderResponse)) {
      return onError(submitOrderResponse.error);
    }
    onSuccess(txId);
    return txId;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    console.error(err);
    onError('Unknown error. Try again later');
  }
}
